<template>
  <div>
    <NuxtLoadingIndicator class="loader" />
    <NavNavigation :green="greenNav" />
    <main id="main" :class="customStyle">
      <slot />
    </main>
    <NavFooterNav />
  </div>
</template>

<script setup lang="ts">
import { useResponsiveStore } from "@/stores/responsiveStore";
const props = defineProps<{
  error?: {
    statusCode: number;
    statusMessage: string;
  };
}>();

const route = useRoute();
const customStyle = ref("");
const greenNav = ref(false);
const mediaQueryList = ref<MediaQueryList | null>();
const responsiveStore = useResponsiveStore();

const getCustomStyle = (path: string) => {
  const contentStylePaths = [
    "blog",
    "help-centre",
    "media-centre",
    "complaint",
    "insights",
  ];
  const enterpriseStylePaths = ["enterprise", "wholesale"];
  const pathArr = path.split("/");

  if (pathArr.some((x) => contentStylePaths.includes(x))) {
    customStyle.value = "content";
  } else if (pathArr.some((x) => enterpriseStylePaths.includes(x))) {
    customStyle.value = "enterprise";
  } else {
    customStyle.value = "";
  }
};

const updateGreenNav = (path: string) => {
  const nonGreenNavPaths = [
    "enterprise",
    "wholesale",
    "blog",
    "help-centre",
    "media-centre",
    "insights",
    "about-us",
    "careers",
    "disaster-support",
    // Migrated pages to DS
    "network",
    "outages",
    "legal",
    "investor-centre",
    "accessibility",
  ];

  const pathArr = path.split("/");
  greenNav.value = !pathArr.some((x) => nonGreenNavPaths.includes(x));
};

const handleMediaQueryChange = (e: MediaQueryListEvent) =>
  (responsiveStore.isDesktop = e.matches);

if (route.path) {
  getCustomStyle(route.path);
  updateGreenNav(route.path);
}

onMounted(() => {
  if (import.meta.client) {
    mediaQueryList.value = window.matchMedia("(min-width: 1024px)");
    responsiveStore.isDesktop = mediaQueryList.value.matches;
    mediaQueryList.value.addEventListener("change", handleMediaQueryChange);
  }
});

onUnmounted(() => {
  if (import.meta.client) {
    mediaQueryList.value?.removeEventListener("change", handleMediaQueryChange);
  }
});

watch(
  () => route.path,
  (newRoute, oldRoute) => {
    if (newRoute === oldRoute) return;

    getCustomStyle(newRoute);
    updateGreenNav(newRoute);
  },
);

const metaStore = useMetaStore();
const pageTitle = computed(() =>
  props.error
    ? `${props.error.statusCode} - ${props.error.statusMessage} | Aussie Broadband`
    : metaStore.pageTitle,
);
const metaTitle = computed(() => metaStore.metaTitle);
const description = computed(() => metaStore.metaDescription);

useHead({
  title: pageTitle,
  link: [
    { rel: "icon", type: "image/svg+xml", href: "/img/logos/favicon.svg" },
    {
      rel: "canonical",
      href: `https://www.aussiebroadband.com.au${route.path || ""}`,
    },
  ],
  meta: [{ name: "theme-color", content: "#13322B" }],
});

useServerSeoMeta({
  title: metaTitle,
  ogTitle: metaTitle,
  description: description,
  ogDescription: description,
  ogImage:
    "https://assets.aussiebroadband.com.au/web/images/misc/abb-og-image.png",
  twitterTitle: metaTitle,
  twitterDescription: description,
  twitterImage:
    "https://assets.aussiebroadband.com.au/web/images/misc/abb-og-image.png",
  twitterSite: "@Aussie_BB",
});
</script>

<style>
@reference "@/assets/css/tailwind.css";

.loader {
  @apply !bg-gradient-to-r from-green-500 to-green-800;
}

.page-enter-active,
.page-leave-active {
  transition: opacity 0.3s;
}
.page-enter-from,
.page-leave-to {
  opacity: 0;
}
</style>
